import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Shield } from 'lucide-react';

const CookieConsent = ({ onOpenTerms }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('stbox-cookie-consent');
    if (!consent) {
      // Small delay before showing the banner
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('stbox-cookie-consent', 'accepted');
    localStorage.setItem('stbox-cookie-consent-date', new Date().toISOString());
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('stbox-cookie-consent', 'declined');
    localStorage.setItem('stbox-cookie-consent-date', new Date().toISOString());
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[90] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden bg-gradient-to-br from-[#2e032e] to-[#1a011a] backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10">
              {/* Gradient glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 blur-xl opacity-50" />

              <div className="relative p-4 md:p-6">
                {/* Close button */}
                <button
                  onClick={handleDecline}
                  className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-white/60" />
                </button>

                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 p-3 bg-pink-500/20 rounded-xl">
                    <Cookie className="w-6 h-6 text-pink-400" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 pr-8 md:pr-0">
                    <h3 className="text-base md:text-lg font-semibold text-white mb-1 flex items-center gap-2">
                      Cookie & Data Notice
                      <Shield className="w-4 h-4 text-green-400" />
                    </h3>
                    <p className="text-sm text-white/70 leading-relaxed">
                      We use cookies and local storage to improve your experience, remember your preferences,
                      and analyze site traffic. By clicking "Accept", you consent to our use of cookies.
                      {onOpenTerms && (
                        <>
                          {' '}See our{' '}
                          <button
                            onClick={onOpenTerms}
                            className="text-pink-400 hover:text-pink-300 underline underline-offset-2 transition-colors"
                          >
                            Terms & Conditions
                          </button>
                          {' '}for more details.
                        </>
                      )}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <button
                      onClick={handleDecline}
                      className="flex-1 md:flex-none px-4 py-2 text-sm font-medium text-white/70 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-all duration-200"
                    >
                      Decline
                    </button>
                    <button
                      onClick={handleAccept}
                      className="flex-1 md:flex-none px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 rounded-lg shadow-lg hover:shadow-pink-500/25 transition-all duration-200"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
